import type { ReactNode } from 'react';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { Icon } from '../../assets/Icon/Icon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantIconColor,
  variantOverlayShadow,
} from '../../common/variant';
import { zIndexes } from '../../common/z_indexes';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { colors, darkThemeSelector, keyframes, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { VStack } from '../../utilities/Stack/VStack';

const toastIconRunningSpin = keyframes({
  '50%': { transform: 'rotate(180deg)' },
  '100%': { transform: 'rotate(360deg)' },
});

const toastIconStyles = {
  width: '$14',
  height: '$14',
  color: variantIconColor,
};

const ToastIconRunning = styled(Icon, {
  ...toastIconStyles,
  animation: `${toastIconRunningSpin} 2s linear infinite`,
});
ToastIconRunning.displayName = 'ToastIconRunning';

const ToastIconLoading = styled(LoadingIcon, {
  ...toastIconStyles,
});
ToastIconLoading.displayName = 'ToastIconLoading';

const ToastIconGlyph = styled(Icon, {
  ...toastIconStyles,
});
ToastIconGlyph.displayName = 'ToastIconGlyph';

const ToastHeading = styled(Body, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  color: variantHeadingColor,
  fontWeight: '$bold',
});
ToastHeading.displayName = 'ToastHeading';

const ToastDescription = styled(Small, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  color: variantBodyColor,
});
ToastDescription.displayName = 'ToastDescription';

const ToastContainer = styled('div', {
  position: 'relative',
  zIndex: zIndexes.alert,
  display: 'flex',
  flexDirection: 'row',
  width: 'max-content',
  maxWidth: '$720',
  padding: '$8 $12',
  backgroundColor: variantBackgroundColor,
  boxShadow: variantOverlayShadow,
  borderRadius: '$10',

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgApplicationDark,
        },

        // Allows to inherit the variant styles without breaking the prior art
        '&': {
          ...getVariantStyles('neutral'),
        },
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
ToastContainer.displayName = 'ToastContainer';

export interface ToastProps {
  /**
   * The actions to display in the toast.
   */
  actions?: ReactNode;
  /**
   * The description to display in the toast.
   */
  description?: ReactNode;
  /**
   * The heading to display in the toast.
   */
  heading: ReactNode;
  /**
   * The icon to display in the toast.
   */
  icon?: IconName;
  /**
   * The state of the toast's related event.
   */
  state?: 'loading' | 'running';
  /**
   * The variant of the toast.
   */
  variant?: VariantProp;
}

function ToastIcon({
  icon,
  state,
}: Omit<ToastProps, 'actions' | 'heading' | 'description' | 'variant'>) {
  if (state === 'loading') {
    return <ToastIconLoading />;
  }
  if (state === 'running') {
    return <ToastIconRunning icon="arrows-rotate" />;
  }
  if (icon) {
    return <ToastIconGlyph icon={icon} />;
  }

  return null;
}

export function Toast<Tag extends React.ElementType>({
  actions,
  description,
  heading,
  icon,
  state,
  ref,
  variant = 'neutral',
  ...props
}: PolymorphicComponentProps<Tag, ToastProps>) {
  return (
    <ToastContainer {...props} ref={ref} variant={variant}>
      <AlignStack
        direction="row"
        gap={{
          start: 6,
          end: 8,
        }}
        preset="body"
        start={(isDefined(icon) || isDefined(state)) && <ToastIcon icon={icon} state={state} />}
        end={
          isDefined(actions) && (
            <ControlGroup relation="separate" size="small">
              {actions}
            </ControlGroup>
          )
        }
      >
        <VStack>
          {heading && <ToastHeading>{heading}</ToastHeading>}
          {description && <ToastDescription>{description}</ToastDescription>}
        </VStack>
      </AlignStack>
    </ToastContainer>
  );
}
